// This is the main LinePlot used in the workspaces and a key performance hotspot.
// When the user mouses over a LinePlot, we draw a "crosshair" (or "flag") with
// the legend in it, and bold the line that the user is mousing over. To do this
// in real-time, we render the LinePlot in layers. There are three layers:
// - The main layer, which renders all line data (including the currently bolded
//   lines but without bolding them).
// - The bold line layer, which renders just the bolded lines.
// - The crosshair layer, which renders the legend.

import React, {
  type FC,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import useResizeObserver from 'use-resize-observer';

import {matchAllMetrics} from '../../state/interactState/constants';
import {useInteractStateWhenOnScreen} from '../../state/interactState/hooks';
import {type Highlight} from '../../state/interactState/types';
import {useIssuesContext} from '../../state/issues/IssuesContext';
import {type IssueLine} from '../../state/issues/types';
import {type PlotFontSize} from '../../util/plotHelpers/plotFontSize';
import {type PlotMargin} from '../../util/plotHelpers/plotHelpers';
import {convertTimestepToSeconds} from '../../util/plotHelpers/time';
import {type Line, type Timestep} from '../../util/plotHelpers/types';
import {Profiler} from '../../util/profiler/react';
import {useRampFlagLinePlotProfiler} from '../../util/rampFeatureFlags';
import {type TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {usePanelConfigContext} from '../PanelRunsLinePlot/PanelConfigContext';
import {type Zoom} from '../PanelRunsLinePlot/types';
import {usePanelZoom2} from '../PanelRunsLinePlot/zoom/PanelZoomContext2';
import {
  type ExcludeOutliersValues,
  isPointVizFullFidelity,
} from '../WorkspaceDrawer/Settings/types';
import {makeClampPoint} from './clampPoint';
import {type BrushEndHandler, type DomainArea} from './highlight';
import * as S from './LinePlot.styles';
import LinePlotCrosshair from './LinePlotCrosshair';
import {LinePlotPlot} from './LinePlotPlot';
import {LinePlotPlotWithHighlighting} from './LinePlotPlotWithHighlighting';
import {PositionedLayer} from './PlotBounds';
import {type Domain, type DomainMaybe} from './types';
import {LinePlotuPlot, useShouldUseuPlot} from './uPlot/LinePlotuPlot';
import {useCalculatedDomain} from './useCalculatedDomain';

interface LinePlotProps {
  children?: React.ReactNode;
  excludeOutliers?: ExcludeOutliersValues;
  filteredLines: Line[];
  fontSize?: PlotFontSize;
  isDrawing: boolean;
  isHovered: boolean;
  issueLines: IssueLine[];
  runNameTruncationType?: TruncationType;
  setIsDrawing: (isDrawing: boolean) => void;
  svg?: boolean;
  timestep?: Timestep;
  xAxis: string; // Name of the xAxis
  xAxisTitle?: string; // optional string for exactly this xaxis title displayed in graph
  xDomain?: DomainMaybe;
  xScale?: 'linear' | 'log';
  yAxis: string; // Name of the yAxis
  yAxisTitle?: string; // optional string for exactly this yaxis title displayed in graph
  yDomain?: DomainMaybe;
  yScale?: 'linear' | 'log';
  zoomCallback?(zoom: Zoom): void;
  zooming?: boolean; // is zooming happening
}

function isNullDomain(domain: DomainMaybe): domain is Domain {
  return domain[0] === null || domain[1] === null;
}

const noOpIssueLines: IssueLine[] = [];

const LinePlotComp: FC<LinePlotProps> = ({
  excludeOutliers,
  filteredLines: linesForOverlay,
  fontSize = 'small',
  isDrawing,
  isHovered,
  issueLines,
  runNameTruncationType,
  setIsDrawing,
  svg,
  timestep,
  xAxis,
  xAxisTitle,
  xDomain: userXDomain,
  xScale = 'linear',
  yAxis,
  yAxisTitle,
  yDomain: userYDomain,
  yScale = 'linear',
  zoomCallback,
}: LinePlotProps) => {
  // state
  const [hideCrosshair, setHideCrosshair] = useState(false);
  const [lastDrawLocation, setLastDrawLocation] = useState<DomainArea | null>(
    null
  );
  const {height, ref: innerRef, width} = useResizeObserver<HTMLDivElement>();

  // hooks
  const {isMultiMetric, pointVisualizationMethod, xAxisIsDatetime, plotType} =
    usePanelConfigContext();
  const [domRef, [activeRun, activeMetric, activeX]] =
    useInteractStateWhenOnScreen(interactState => [
      interactState.highlight['run:name'] as string | undefined,
      interactState.highlight['run:metric'] as Highlight['run:metric'],
      interactState.highlight[xAxis] as number,
    ]);
  const {hoveredIssue} = useIssuesContext();
  const {queryZoom} = usePanelZoom2();

  // derived values
  const isHeatmap = linesForOverlay[0]?.type === 'heatmap';
  const [calcXDomain, calcYDomain] = useCalculatedDomain({
    filteredLines: linesForOverlay,
    excludeOutliers,
    isFullFidelityMode: isPointVizFullFidelity(pointVisualizationMethod),
    isHeatmap,
    userXDomain,
    userYDomain,
  });

  /**
   * TODO: These are cast as Domain but the types are actually DomainMaybe
   */
  const notNullXDomain = calcXDomain as Domain;
  const notNullYDomain = calcYDomain as Domain;

  const crosshairXDomain = useMemo(() => {
    return isHeatmap
      ? [notNullXDomain[0] - 0.5, notNullXDomain[1] + 0.5]
      : notNullXDomain;
  }, [isHeatmap, notNullXDomain]);

  /**
   * Notes on point clamping:
   * There are a couple of different scenarios that point clamping is intended to address:
   * 1a) If non-finite values are fed into the chart (Inf, -Inf) we need to avoid them blowing out chart scaling so you can't see the line
   * 1b) Similarly, if just a couple really out of bounds outlier values come in, we need to avoid those blowing out the chart scaling too
   * 2) If a user has provided explicit boundaries on the chart (either w/ config values or with a zoom event) we need to fix _exactly_ to those boundaries so that chart points don't render outside the chart area.
   *
   * TODO: investigate if the 1b scenario still is possible given changes to exclude outliers mode
   */
  const pointClamper = useMemo(() => {
    const safeYDomain = calcYDomain as Domain;

    return makeClampPoint(
      [safeYDomain[0], safeYDomain[1]],
      userYDomain
        ? [safeYDomain[0], safeYDomain[1]]
        : [safeYDomain[0] - 100000, safeYDomain[1] + 100000] // these are the fudged boundaries to avoid
    );
  }, [calcYDomain, userYDomain]);

  // After calculating the domain, let's filter the lines a bit again. We want to avoid
  // a bug (https://github.com/wandb/core/issues/2323) where lines aren't drawn when
  // there are extreme outliers present. So let's clamp points to an offset of the domain.
  // We'll still use the unclamped points in the crosshair so as to not obviously present "wrong" data.
  const clampedFilteredLines: Line[] = useMemo(
    function clampLineValues() {
      if (isNullDomain(calcYDomain)) {
        return linesForOverlay;
      }
      // we can do this because we checked for nulls above

      const returnLines = linesForOverlay.map(function mapClampedPoints(line) {
        return {
          ...line,
          data: line.data.map(pointClamper),
          // there are no nan points on the aux lines
          nanPoints: (line.nanPoints ?? []).map(pointClamper),
        };
      });
      return returnLines;
    },
    [linesForOverlay, calcYDomain, pointClamper]
  );

  // if user passed in multiple heatmaps only display the first one and nothing else
  const linesOrHeatmap = useMemo(
    function linesOrHeatMapMemo() {
      const heatmap = clampedFilteredLines.find(
        line => line.type === 'heatmap'
      );
      if (heatmap == null) {
        return clampedFilteredLines;
      }
      return [heatmap];
    },
    [clampedFilteredLines]
  );

  const handleCrosshairBrushEnd: BrushEndHandler = useCallback(
    function handleCrossHairBrushEndCallback(
      area,
      newZoomedYAxis,
      {interrupt}: {interrupt?: boolean} = {}
    ) {
      if (interrupt) {
        return;
      }
      setLastDrawLocation(area);

      if (zoomCallback != null) {
        let left = area?.left;
        let right = area?.right;
        if (timestep != null) {
          if (left != null) {
            left = convertTimestepToSeconds(left, timestep);
          }
          if (right != null) {
            right = convertTimestepToSeconds(right, timestep);
          }
        } else if (xAxisIsDatetime) {
          left = left == null ? left : left / 1000;
          right = right == null ? right : right / 1000;
        }

        // only pass the area if the zoom callback gives a specific y Zoom range
        // passing undefined will let the chart autoscale to the range of the shown y-axis
        zoomCallback({
          xAxisMin: left,
          xAxisMax: right,
          yAxisMin: newZoomedYAxis ? area?.bottom : undefined,
          yAxisMax: newZoomedYAxis ? area?.top : undefined,
        });
      }
    },
    [timestep, zoomCallback, xAxisIsDatetime]
  );

  const handleCrosshairMouseUp = useCallback(() => {
    setHideCrosshair(false);
  }, []);
  const handleCrosshairMouseDown = useCallback(() => {
    setHideCrosshair(true);
  }, []);

  /**
   * Dimming exists if:
   * - there is a valid run in an active hover state
   * - we are not in a single run view
   * - and the feature flag is flipped
   */
  const isDimmed = !!activeRun;
  const dimFactor = !isDimmed ? undefined : 0.3;

  const dataOutOfBoundsX = dataOutOfBounds(notNullXDomain, userXDomain);
  const dataOutOfBoundsY = dataOutOfBounds(notNullYDomain, userYDomain);

  const linesForHighlighting = React.useMemo(
    function getLinesForHighlighting() {
      const highlightedLines = linesOrHeatmap.filter(l => {
        // Include the actively hovered run
        const isActiveRun = l.uniqueId === activeRun || l.name === activeRun;
        // Also include baseline run to keep it at full opacity
        // Note: pinned runs should dim like other non-hovered runs
        const isBaseline = l.run?.isBaseline;

        if (isMultiMetric) {
          const isActiveMetric =
            l.metricName === activeMetric || activeMetric === matchAllMetrics;
          return (isActiveRun || isBaseline) && isActiveMetric;
        }

        return isActiveRun || isBaseline;
      });

      return highlightedLines;
    },
    [activeRun, activeMetric, linesOrHeatmap, isMultiMetric]
  );

  const {
    shouldUseuPlot,
    areAllLinesuPlotSupported,
    isChartTypeuPlotSupported,
    forceReactVis,
  } = useShouldUseuPlot(linesOrHeatmap, plotType);

  const isLinePlotProfilerEnabled = useRampFlagLinePlotProfiler();
  const extraContext = useMemo(() => {
    if (isLinePlotProfilerEnabled) {
      // only calculate these values if the sampling feature flag is enabled
      return {
        numLines: linesOrHeatmap.filter(line => line.type === 'line').length,
        numAreas: linesOrHeatmap.filter(line => line.type === 'area').length,
        maxNumPoints:
          linesOrHeatmap.length > 0
            ? Math.max(...linesOrHeatmap.map(line => line.data.length))
            : 0,
        areAllLinesuPlotSupported,
        isChartTypeuPlotSupported,
        forceReactVis,
      };
    }
    return {};
  }, [
    isLinePlotProfilerEnabled,
    linesOrHeatmap,
    areAllLinesuPlotSupported,
    isChartTypeuPlotSupported,
    forceReactVis,
  ]);

  const [plotMarginOverride, setPlotMarginOverride] = useState<
    PlotMargin | undefined
  >();

  if (linesForOverlay.length === 0) {
    return null;
  }

  if (
    calcXDomain[0] == null ||
    calcXDomain[1] == null ||
    calcYDomain[0] == null ||
    calcYDomain[1] == null
  ) {
    return <S.InvalidDataAlert>Unable to process data.</S.InvalidDataAlert>;
  }

  if (dataOutOfBoundsX || dataOutOfBoundsY) {
    return (
      <S.InvalidDataAlert>
        Data out of bounds. Try adjusting the axis range.
      </S.InvalidDataAlert>
    );
  }

  if (shouldUseuPlot) {
    // if the height or width is not measured yet, we need to return a placeholder div because uPlot needs the correct dimensions
    // in order to render the plot.
    if (height == null || width == null) {
      return <div ref={innerRef} style={{width: '100%', height: '100%'}} />;
    }

    // For uPlot with log scale, clamp domain to positive values (log(0) is undefined)
    const LOG_SCALE_MIN = 1e-4;
    const uPlotCrosshairXDomain =
      xScale === 'log'
        ? [
            Math.max(crosshairXDomain[0], LOG_SCALE_MIN),
            Math.max(crosshairXDomain[1], LOG_SCALE_MIN),
          ]
        : crosshairXDomain;
    const sharedProps = {
      width,
      height,
      xDomain: calcXDomain,
      yDomain: calcYDomain,
      xAxis,
      yAxisTitle,
      fontSize,
      timestep,
      plotMarginOverride,
    };

    // Use three layers like react-vis:
    // 1. Base layer with all lines (dimmed via opacity)
    // 2. Highlighted layer with only highlighted lines (on top)
    // 3. Tooltip layer (on top of everything, not dimmed)
    return (
      <Profiler
        enabled={isLinePlotProfilerEnabled ?? false}
        id="uPlot-profiler"
        extraContext={extraContext}>
        <PositionedLayer ref={domRef}>
          <style>
            {`
            /*
              We don't have direct access to React-vis crosshair box so we have to CSS it
              These rules keep the overlay from overlapping the vertical bar showing the
              intersection of the x-axis and the lines
            */
            .rv-crosshair__inner--right {
              left: 12px;
            }

            .rv-crosshair__inner--left {
              right: 12px;
            }
          `}
          </style>
          {/* Base layer with all lines - dimmed when there are highlighted lines, no tooltip */}
          <PositionedLayer ref={innerRef} dim={dimFactor}>
            <LinePlotuPlot
              lines={linesOrHeatmap}
              onZoom={zoomCallback}
              issueLines={issueLines}
              hoveredIssue={hoveredIssue}
              isZoomedXMin={queryZoom.xAxisMin != null}
              isZoomedXMax={queryZoom.xAxisMax != null}
              dimFactor={dimFactor}
              setPlotMarginOverride={setPlotMarginOverride}
              {...sharedProps}
            />
          </PositionedLayer>

          {/* Highlighted layer - only contains highlighted/baseline lines, rendered on top */}
          {linesForHighlighting.length > 0 && (
            <PositionedLayer pointerEvents="none">
              <LinePlotuPlot
                lines={linesForHighlighting}
                issueLines={[]}
                isOverlay
                {...sharedProps}
              />
            </PositionedLayer>
          )}

          {/* Tooltip layer - on top of everything, not dimmed */}
          <PositionedLayer>
            {height !== 0 && (
              <LinePlotCrosshair
                activeMetric={activeMetric}
                activeRun={activeRun}
                activeX={activeX}
                fontSize={fontSize}
                height={height || 200}
                hideCrosshair={hideCrosshair}
                isDrawing={isDrawing}
                isHovered={isHovered ?? false}
                lastDrawLocation={lastDrawLocation}
                lines={linesForOverlay}
                onBrushEnd={handleCrosshairBrushEnd}
                onMouseDown={handleCrosshairMouseDown}
                onMouseUp={handleCrosshairMouseUp}
                plotMargin={plotMarginOverride}
                pointClamper={pointClamper}
                runNameTruncationType={runNameTruncationType}
                setIsDrawing={setIsDrawing}
                width={width || 200}
                xAxis={xAxis}
                xDomain={uPlotCrosshairXDomain}
                xScale={xScale}
                yAxis={yAxis}
                yDomain={notNullYDomain}
                yScale={yScale}
              />
            )}
          </PositionedLayer>
        </PositionedLayer>
      </Profiler>
    );
  }

  return (
    <Profiler
      enabled={isLinePlotProfilerEnabled ?? false}
      id="react-vis-profiler"
      extraContext={extraContext}>
      <PositionedLayer ref={domRef}>
        <style>
          {`
          /*
            We don't have direct access to React-vis crosshair box so we have to CSS it
            These rules keep the overlay from overlapping the vertical bar showing the
            intersection of the x-axis and the lines
          */
          .rv-crosshair__inner--right {
            left: 12px;
          }

          .rv-crosshair__inner--left {
            right: 12px;
          }
        `}
        </style>
        {/**
         * Note: this used to be wrapped in DelayedRender which was originally intended for a bug where React Vis wouldn't size correctly when siblings were added and removed to the component containing React-Vis (https://github.com/laxels/flex-grow-react-sibling). I believe this was the legend, but I've moved that up a level and have reason to believe that the original conditions requiring DelayedRender no longer exist.
         *
         * IF we get a bug where React Vis isn't sizing correctly in the line-plots please see:
         * https://github.com/wandb/core/pull/18454
         *
         * For performance we render multiple layers. This way we don't have to re-render the entire plot when the highlighted lines change.
         */}

        <PositionedLayer dim={dimFactor} ref={innerRef}>
          {height !== 0 && (
            <LinePlotPlot
              drawing={isDrawing}
              fontSize={fontSize}
              height={height || 200}
              issueLines={issueLines}
              lines={linesOrHeatmap}
              svg={svg}
              width={width || 200}
              xAxis={xAxis}
              xAxisTitle={xAxisTitle}
              xDomain={notNullXDomain}
              xScale={xScale}
              yAxis={yAxis}
              yAxisTitle={yAxisTitle}
              yDomain={notNullYDomain}
              yScale={yScale}
              timestep={timestep}
            />
          )}
        </PositionedLayer>

        <PositionedLayer>
          {height !== 0 && (
            <LinePlotPlotWithHighlighting
              drawing={isDrawing}
              fontSize={fontSize}
              height={height || 200}
              isHovered={isHovered}
              issueLines={noOpIssueLines}
              lines={linesForHighlighting}
              svg={svg}
              width={width || 200}
              xAxis={xAxis}
              xAxisTitle={xAxisTitle}
              xDomain={notNullXDomain}
              xScale={xScale}
              yAxis={yAxis}
              yAxisTitle={yAxisTitle}
              yDomain={notNullYDomain}
              yScale={yScale}
              timestep={timestep}
            />
          )}
        </PositionedLayer>

        <PositionedLayer>
          {height !== 0 && (
            <LinePlotCrosshair
              activeMetric={activeMetric}
              activeRun={activeRun}
              activeX={activeX}
              fontSize={fontSize}
              height={height || 200}
              hideCrosshair={hideCrosshair}
              isDrawing={isDrawing}
              isHovered={isHovered ?? false}
              lastDrawLocation={lastDrawLocation}
              lines={linesForOverlay}
              onBrushEnd={handleCrosshairBrushEnd}
              onMouseDown={handleCrosshairMouseDown}
              onMouseUp={handleCrosshairMouseUp}
              pointClamper={pointClamper}
              runNameTruncationType={runNameTruncationType}
              setIsDrawing={setIsDrawing}
              width={width || 200}
              xAxis={xAxis}
              xDomain={crosshairXDomain}
              xScale={xScale}
              yAxis={yAxis}
              yDomain={notNullYDomain}
              yScale={yScale}
            />
          )}
        </PositionedLayer>
      </PositionedLayer>
    </Profiler>
  );
};

export const LinePlot = memo(LinePlotComp);

function dataOutOfBounds(calcDomain: Domain, userDomain?: DomainMaybe) {
  if (userDomain == null || calcDomain == null) {
    return false;
  }

  const [boundsMin, boundsMax] = userDomain;
  const [dataMin, dataMax] = calcDomain;

  const boundsMaxSmallerThanDataMin =
    boundsMax != null && dataMin != null && boundsMax < dataMin;
  const boundsMinBiggerThanDataMax =
    boundsMin != null && dataMax != null && boundsMin > dataMax;

  return boundsMaxSmallerThanDataMin || boundsMinBiggerThanDataMax;
}
